<template>
  <RfDialog
    ref="newPromptDialogRef"
    variant="outlined"
    closeOnClickOutside
    confirmText="Create"
    width="1220"
    height="762"
    :disabled="isConfirmDisabled"
    :loading="isLoading"
    title="New Prompt"
    @confirm="handleCreate"
    @close="resetState()"
  >
    <div class="flex h-full min-w-px overflow-hidden">
      <RfVerticalList
        v-model="deviceType"
        :items="DeviceTypeWording"
        :disabled="
          props.lockedDeviceType
            ? `All prompts in this guide must use the ${DeviceTypeWording[props.lockedDeviceType]?.fullTitle || DeviceTypeWording[props.lockedDeviceType]?.text || props.lockedDeviceType} device type`
            : false
        "
        class="shrink-0 grow-0 p-8 pl-6 whitespace-nowrap"
        listTitle="Device Type"
        :titleKey="d => d.fullTitle || d.text"
        iconKey="icon"
        valueKey="value"
        @update:modelValue="resetState({ promptType: true, newPrompt: true })"
        @vue:mounted="!props.lockedDeviceType && (deviceType = DeviceTypeWording.web)"
      />
      <div class="bg-beige-10 w-px"></div>
      <div class="w-full overflow-auto p-8">
        <div class="flex flex-col gap-4">
          <RfAlert v-if="deviceType?.description" type="info">{{ deviceType.description }}</RfAlert>
          <template v-if="!props.sequenceId && appsStore.currAppTemplates.loading">
            <h3 class="text-heading-2 pb-2 pl-2 capitalize">Templates</h3>
            <div class="grid grid-cols-3 gap-2">
              <div
                v-for="i in 3"
                :key="i"
                class="border-beige-10 flex flex-col gap-2 rounded-lg border border-solid p-4"
              >
                <RfBaseSkeleton fullWidth fullHeight :radius="4" class="aspect-3/2" />
                <RfBaseSkeleton :height="14" :radius="4" class="w-3/4" />
                <RfBaseSkeleton :height="10" :radius="4" class="w-1/2" />
              </div>
            </div>
          </template>
          <RfCardList
            v-if="deviceType"
            v-model="promptType"
            :items="promptList"
            titleKey="label"
            descriptionKey="description"
            tagsKey="tags"
            @update:modelValue="
              v => {
                promptType = v || null;
                resetState({
                  newPrompt: true,
                  engagementCategory: v?.tags?.[0]?.value,
                  engagementSubcategory: v?.engagement_subcategory,
                  isTemplate: v?.isTemplate,
                });
              }
            "
          />
        </div>
      </div>
      <Transition
        enterActiveClass="transition-transform duration-300 ease-out"
        enterFromClass="translate-x-full"
        enterToClass="translate-x-0"
        leaveActiveClass="transition-transform duration-300 ease-in"
        leaveFromClass="translate-x-0"
        leaveToClass="translate-x-full"
      >
        <div v-if="deviceType.value && promptType?.value" class="flex h-full">
          <div class="bg-beige-10 w-px"></div>
          <div class="bg-beige-0 flex w-72 shrink-0 grow-0 flex-col gap-4 p-8">
            <div>
              <h3 class="text-heading-2 truncate">{{ deviceType.text }} {{ promptType?.label }}</h3>
              <p v-if="newPrompt.isTemplate" class="text-body text-beige-50">
                {{ ENGAGEMENT_CATEGORIES[newPrompt.engagementCategory]?.label }}
              </p>
            </div>
            <div v-if="showZoneSelection && !availableZones.length" class="flex flex-col gap-4">
              <p class="text-body text-black">
                This prompt type requires zones. A zone marks a specific location where Recurly
                Engage can inject personalized content.
              </p>
              <div class="flex gap-2">
                <RfButtonNew
                  type="tertiary"
                  class="self-start"
                  size="small"
                  :to="{ path: `/apps/${appsStore.app.id}/settings/zones` }"
                >
                  Manage Zones
                </RfButtonNew>
              </div>
            </div>
            <div v-else class="flex flex-col gap-4">
              <RfInput
                v-model="newPrompt.name"
                title="Name"
                placeholder="Enter a name"
                requiredTitle
                variant="outlined"
                autoWidth
                class="flex-1"
              />
              <RfInput
                v-model="newPrompt.description"
                title="Description"
                placeholder="Details about this prompt"
                variant="outlined"
                autoWidth
                class="flex-1"
              />
              <RfSelect
                v-if="showEngagementCategoryField && !newPrompt.isTemplate && !props.sequenceId"
                v-model="newPrompt.engagementCategory"
                title="Engagement Category"
                placeholder="Select an engagement category"
                preset="modalMenu"
                :items="engagementCategories"
                variant="outlined"
                :itemDisabled="item => item.disabled"
                :itemDisabledMessage="item => item.disabledMessage"
                sameWidth
                autoWidth
                itemValue="label"
                class="flex-1"
              >
                <template #selected="{ props: tag }">
                  <RfColorTag
                    :title="tag.label"
                    pill
                    disabled
                    :backgroundColor="getCategoryColor(tag.value)"
                  />
                </template>
                <template #item="{ props: tag }">
                  <div class="flex flex-col items-start">
                    <RfColorTag
                      :title="tag.label"
                      pill
                      disabled
                      :backgroundColor="getCategoryColor(tag.value)"
                    />
                    <span class="text-grey-2 ml-2 text-sm">{{ tag.description }}</span>
                  </div>
                </template>
              </RfSelect>
              <RfRadioGroup
                v-if="showRetentionTypeField"
                v-model="retentionTypeRadio"
                legend="Retention type"
                requiredTitle
                vertical
                :options="retentionTypeOptions"
                class="[&_.rf-radio-group--wrapper]:gap-y-4"
              />
              <div v-if="showRetentionTypeField" class="text-caption text-grey-2 mt-1">
                Optional — select "None" if this prompt has no retention type.
              </div>
              <RfSelect
                v-if="deviceType.value === DeviceTypeWording.custom_defined.value"
                v-model="newPrompt.customDevices"
                title="Custom device(s)"
                placeholder="Select custom device(s)"
                :items="customDevicesStore.customDevices.filter(d => d.label !== PushDevice.value)"
                variant="outlined"
                itemKey="name"
                itemValue="label"
                sameWidth
                multiple
                :disabled="isPushPromptType"
                autoWidth
                class="flex-1"
              >
              </RfSelect>

              <RfSelect
                v-if="showZoneSelection"
                v-model="newPrompt.zone"
                title="Zone"
                placeholder="Select where this prompt appears on the screen"
                :items="availableZones"
                variant="outlined"
                sameWidth
                requiredTitle
                autoWidth
                class="flex-1"
              >
              </RfSelect>
            </div>
          </div>
        </div>
      </Transition>
    </div>
  </RfDialog>
</template>

<script setup>
import RfCardList from "@/blocks/RfLists/RfCardList.vue";
import RfVerticalList from "@/blocks/RfLists/RfVerticalList.vue";
import RfDialog from "@/components/modals/RfDialog.vue";
import RfButtonNew from "@/components/buttons/RfButtonNew.vue";
import RfInput from "@/components/inputs/RfInput.vue";
import RfSelect from "@/components/inputs/RfSelect.vue";
import RfRadioGroup from "@/components/inputs/RfRadioGroup.vue";
import RfAlert from "@/components/alerts/RfAlert.vue";
import RfBaseSkeleton from "@/components/skeletons/RfBaseSkeleton.vue";
import RfColorTag from "@/components/tags/RfColorTag.vue";
import { cloneDeep } from "lodash-es";
import { useAllPromptsStore } from "@/pinia/allPromptsStore";
import { useAppsStore } from "@/pinia/appsStore";
import { useCustomDevicesStore } from "@/pinia/customDevicesStore";
import { useLegacyTmpStore } from "@/pinia/legacyTmpStore";
import { usePromptStore } from "@/pinia/promptStore";
import { useToastsStore } from "@/pinia/toastsStore";
import { DeviceTypeWording, PushDevice } from "@/utils/constants/DevicesConstants";
import {
  ENGAGEMENT_CATEGORIES,
  getCategoryColor,
} from "@/utils/constants/EngagementCategoryConstants";
import { PromptTypes } from "@/utils/constants/PromoTypesConstants";
import { computed, onMounted, ref, useTemplateRef, watch } from "vue";
import { useRouter } from "vue-router";
import { useRecentActivity } from "@/utils/composables/useRecentActivity";
import { getPromptLink, getGuidePromptLink } from "@/utils/getLink";
import { useEngagementCategories } from "@/utils/composables/useEngagementCategories";
import { useEngagementSubcategories } from "@/utils/composables/useEngagementSubcategories";

const props = defineProps({
  sequenceId: { type: String, default: null },
  sequenceOrder: { type: Number, default: null },
  lockedDeviceType: { type: String, default: null },
});

const emit = defineEmits(["created"]);
const appsStore = useAppsStore();
const allPromptStore = useAllPromptsStore();
const customDevicesStore = useCustomDevicesStore();
const legacyTmpStore = useLegacyTmpStore();
const promptStore = usePromptStore();
const toastsStore = useToastsStore();
const router = useRouter();
const recentActivity = useRecentActivity();
const newPromptDialogRef = useTemplateRef("newPromptDialogRef");
const isLoading = ref(false);
const deviceType = ref(
  props.lockedDeviceType ? (DeviceTypeWording[props.lockedDeviceType] ?? {}) : {},
);
const promptType = ref(null);

const newPrompt = ref({
  name: "",
  description: "",
  engagementCategory: "",
  engagementSubcategory: undefined,
  customDevices: [],
  zone: null,
});

const {
  engagementCategoriesEnabled: showEngagementCategoryField,
  engagementCategoriesWithRestrictions: engagementCategories,
} = useEngagementCategories();
const { isSubcategoryAllowedFor, radioOptionsForCategory } = useEngagementSubcategories();

// Subcategory is inherited from the guide for in-sequence prompts, so it is only
// selectable when creating a standalone prompt
const showRetentionTypeField = computed(
  () =>
    showEngagementCategoryField.value &&
    !newPrompt.value.isTemplate &&
    !props.sequenceId &&
    newPrompt.value.engagementCategory === "retention",
);

// "none" is the radio sentinel (mirrors RfBulkEditModal); null is the emitted
// no-subcategory value. undefined means no choice made yet, which the Create
// gate requires picking one of the three options. radioOptionsForCategory is
// the same options source the bulk edit uses, so descriptions render identically.
const retentionTypeOptions = computed(() => radioOptionsForCategory("retention"));

const retentionTypeRadio = computed({
  get: () =>
    newPrompt.value.engagementSubcategory === undefined
      ? undefined
      : (newPrompt.value.engagementSubcategory ?? "none"),
  set: value => {
    newPrompt.value.engagementSubcategory = value === "none" ? null : value;
  },
});

const showZoneSelection = computed(() =>
  [
    PromptTypes.horizontal.value,
    PromptTypes.vertical.value,
    PromptTypes.tile.value,
    PromptTypes.text.value,
  ].includes(promptType.value?.value),
);

const isPushPromptType = computed(
  () => promptType.value?.value === PromptTypes.scheduled_push.value,
);

watch(
  () => props.lockedDeviceType,
  newVal => {
    if (newVal) {
      if (deviceType.value.value !== newVal) deviceType.value = DeviceTypeWording[newVal] ?? {};
    } else {
      deviceType.value = DeviceTypeWording.web;
    }
  },
);

watch(isPushPromptType, isPush => {
  if (isPush) {
    const pushDevice = customDevicesStore.customDevices.find(d => d.label === PushDevice.value);
    if (pushDevice) newPrompt.value.customDevices = [pushDevice.name];
  } else {
    newPrompt.value.customDevices = [];
  }
});

const PROMPT_SECTION_TITLES = { templates: "Templates", custom: "Custom Prompts" };

const promptList = computed(() => {
  const res = {};
  if (!props.sequenceId) {
    const apiTemplates = appsStore.currAppTemplates.value;
    if (Array.isArray(apiTemplates) && apiTemplates.length) {
      const filtered = apiTemplates.filter(t => t.device_type === deviceType.value.value);
      if (filtered.length) {
        res.templates = filtered.map(t => ({
          label: (t.name || "").replaceAll("DEFAULT - ", "").trim(),
          value: t.key,
          description: t.description,
          tags: [ENGAGEMENT_CATEGORIES[t.engagement_category]].filter(Boolean),
          engagement_subcategory: t.engagement_subcategory,
          path_type: t.path_type,
          actions: t.actions,
          isTemplate: true,
        }));
      }
    }
  }

  res.custom = [
    PromptTypes.horizontal,
    PromptTypes.vertical,
    PromptTypes.tile,
    PromptTypes.text,
    PromptTypes.widget,
    PromptTypes.interstitial,
    PromptTypes.retention_modal,
    PromptTypes.video,
    PromptTypes.invisible,
  ];
  if (
    deviceType.value.value === DeviceTypeWording.web.value ||
    appsStore.app?.flags?.banner_for_non_web_devices
  ) {
    res.custom.push(PromptTypes.bottom_banner);
  }

  if (
    deviceType.value.value === DeviceTypeWording.custom_defined.value &&
    appsStore.app?.flags?.scheduled_push
  ) {
    res.custom.push(PromptTypes.scheduled_push);
  }

  if (appsStore.app?.flags?.email_path_enabled) {
    res.custom.push(PromptTypes.email);
  }

  return Object.fromEntries(
    Object.entries(res).map(([key, value]) => [PROMPT_SECTION_TITLES[key] ?? key, value]),
  );
});

const availableZones = computed(() => {
  return allPromptStore.promptGroups
    .filter(group => group.device_type === deviceType.value.value)
    .filter(group => group.path_type === promptType.value?.value)
    .toSorted((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
    .map(item => ({ text: item.name || item.zone, value: item.id }));
});

const isConfirmDisabled = computed(() => {
  if (!newPrompt.value.name) return "Name is required";
  return (
    (showZoneSelection.value && !newPrompt.value.zone) ||
    (showEngagementCategoryField.value &&
      !props.sequenceId &&
      !newPrompt.value.engagementCategory &&
      "Engagement category is required") ||
    // undefined = no pick yet; null = "None" picked, which counts as a choice
    (showRetentionTypeField.value &&
      newPrompt.value.engagementSubcategory === undefined &&
      "Retention type is required")
  );
});

watch(
  () => newPrompt.value.engagementCategory,
  category => {
    if (category !== "retention") newPrompt.value.engagementSubcategory = undefined;
  },
);

watch(availableZones, zones => {
  if (zones.length && !newPrompt.value.zone) {
    newPrompt.value.zone = zones[0].value;
  }
});

const handleCreate = async () => {
  const { name, description, engagementCategory, engagementSubcategory, customDevices, zone } =
    newPrompt.value;
  const pathType = promptType.value?.path_type ?? promptType.value?.value;

  const templateActions = promptType.value?.actions ?? null;
  const deviceTypeVal = deviceType.value.value;
  const appId = appsStore.app.id;
  const customDeviceIds = customDevicesStore.customDevices
    .filter(d => customDevices.includes(d.name))
    .map(d => d.id);
  const sequenceFields = props.sequenceId
    ? { sequence_id: props.sequenceId, sequence_order: props.sequenceOrder }
    : {};
  const subcategoryFields =
    !props.sequenceId && engagementCategory === "retention"
      ? { engagement_subcategory: engagementSubcategory }
      : {};

  isLoading.value = true;
  try {
    let result;
    if (zone) {
      const pathGroup = cloneDeep(allPromptStore.promptGroups.find(g => g.id === zone));
      await legacyTmpStore.getRetentionModel({
        appId,
        pathType,
        deviceType: deviceTypeVal,
        iab: pathGroup.settings.iab,
        customDeviceIds,
        sequenceId: props.sequenceId,
      });
      const retentionBase = cloneDeep(legacyTmpStore.retentionModel);
      const newPath = {
        ...retentionBase,
        ...(templateActions && { actions: { ...retentionBase?.actions, ...templateActions } }),
        name,
        description,
        engagement_category: engagementCategory,
        ...subcategoryFields,
        custom_device_ids: customDeviceIds,
        ...sequenceFields,
      };
      pathGroup.paths = [...pathGroup.paths, newPath];
      result = await promptStore.updatePathGroup({ appId, model: pathGroup, isLoading: true });
    } else {
      await Promise.all([
        legacyTmpStore.getRetentionModel({
          appId,
          pathType,
          deviceType: deviceTypeVal,
          customDeviceIds,
          sequenceId: props.sequenceId,
        }),
        legacyTmpStore.getPathGroupModel({ appId, pathType, deviceType: deviceTypeVal }),
      ]);
      const retentionBase = cloneDeep(legacyTmpStore.retentionModel);
      const newRetention = {
        ...retentionBase,
        ...(templateActions && { actions: { ...retentionBase?.actions, ...templateActions } }),
        name,
        description,
        engagement_category: engagementCategory,
        ...subcategoryFields,
        custom_device_ids: customDeviceIds,
        ...sequenceFields,
      };
      const newPathGroup = cloneDeep(legacyTmpStore.pathGroup);
      newPathGroup.paths = [newRetention];
      newPathGroup.name = `${name} Placement`;
      newPathGroup.device_type = deviceTypeVal;
      newPathGroup.path_type = pathType;
      result = await promptStore.createPathGroup({ appId, model: newPathGroup });
    }
    const pathId = result?.lastNewPromptId;
    if (pathId) {
      recentActivity.updatePathActivity(appId, pathId);
      if (props.sequenceId) {
        router.push({ path: getGuidePromptLink(pathId, props.sequenceId, appId) });
      } else {
        router.replace({ path: getPromptLink(pathId, appId) });
      }
    }
    emit("created");
    resetState();
  } catch (e) {
    toastsStore.create({ type: "error", body: e.message });
  } finally {
    isLoading.value = false;
  }
};

const resetState = options => {
  if (!options || options.deviceType)
    deviceType.value = props.lockedDeviceType
      ? (DeviceTypeWording[props.lockedDeviceType] ?? {})
      : {};
  if (!options || options.promptType) promptType.value = null;
  if (!options || options.newPrompt) {
    const engagementCategory =
      options?.engagementCategory ?? Object.values(ENGAGEMENT_CATEGORIES)[0].value;
    newPrompt.value = {
      name: "",
      description: "",
      engagementCategory,
      // Template cards carry their engagement_subcategory; seed it only when the
      // category allows that value. Custom prompts start unpicked (undefined) so
      // the retention type stays required.
      engagementSubcategory: options?.isTemplate
        ? isSubcategoryAllowedFor(engagementCategory, options?.engagementSubcategory)
          ? (options?.engagementSubcategory ?? null)
          : null
        : undefined,
      customDevices: [],
      zone: null,
      isTemplate: options?.isTemplate || false,
    };
  }
};

onMounted(() => {
  customDevicesStore.getCustomDevices(appsStore.app.id);
  allPromptStore.getPromptGroups(appsStore.app.id, { nonBlocking: true });
});

defineExpose({
  open: () => newPromptDialogRef.value?.open(),
  close: () => newPromptDialogRef.value?.close(),
});
</script>
